/**
 * Algolia Custom Autocomplete Integration for Shopify Doss
 * Fully custom implementation using @algolia/autocomplete-js and algoliasearch
 */
document.addEventListener('DOMContentLoaded', () => {
  const initAutocomplete = () => {
    const config = window.customAlgoliaConfig;
    if (!config || !config.appId || !config.apiKey) {
      console.warn('Algolia Custom Autocomplete: Configuration or credentials missing.');
      return;
    }

    // Wait for CDN libraries to load in the browser
    if (!window.algoliasearch || !window['@algolia/autocomplete-js']) {
      setTimeout(initAutocomplete, 100);
      return;
    }

    // The Algolia CDN app also initializes autocomplete on .search-bar.
    // We must run AFTER it so our instance wins. Detect CDN by its redirectUrlPlugin
    // in aria-controls, then remove its element before initializing ours.
    const existingInput = document.querySelector('.search-bar .aa-Input');
    const ariaControls = existingInput ? (existingInput.getAttribute('aria-controls') || '') : '';
    const cdnHasInitialized = ariaControls.includes('redirectUrlPlugin');

    if (!cdnHasInitialized) {
      // CDN not ready yet — wait and retry (max ~1.5s total)
      if ((initAutocomplete._attempts || 0) < 8) {
        initAutocomplete._attempts = (initAutocomplete._attempts || 0) + 1;
        setTimeout(initAutocomplete, 200);
        return;
      }
      // Fallback: proceed anyway after timeout
    }

    // Remove CDN's autocomplete element so we can mount ours cleanly
    document.querySelectorAll('.search-bar .aa-Autocomplete').forEach(function(el) {
      el.remove();
    });
    initAutocomplete._attempts = 0;

    const { autocomplete, getAlgoliaResults } = window['@algolia/autocomplete-js'];
    const searchClient = window.algoliasearch(config.appId, config.apiKey);

    console.log('🚀 Custom Algolia Autocomplete: Initializing with config:', config);

    // Format price to IDR currency style (e.g. Rp 150.000)
    const formatPrice = (price) => {
      if (typeof price === 'string') price = parseFloat(price);
      if (isNaN(price)) return '';
      
      // Shopify Algolia prices are usually stored in major currency units (e.g. 150000.0)
      // We format it according to Indonesian standards
      return 'Rp ' + Math.round(price).toLocaleString('id-ID');
    };

    // Helper to manage recent searches in localStorage
    const getRecentSearches = () => {
      try {
        return JSON.parse(localStorage.getItem('algolia_recent_searches') || '[]');
      } catch (e) {
        return [];
      }
    };

    const addRecentSearch = (query) => {
      if (!query || !query.trim()) return;
      console.log('💾 Custom Algolia Autocomplete: Saving query to recent searches:', query);
      let searches = getRecentSearches();
      searches = searches.filter(item => item.toLowerCase() !== query.toLowerCase());
      searches.unshift(query.trim());
      searches = searches.slice(0, 5); // Limit to 5 items
      localStorage.setItem('algolia_recent_searches', JSON.stringify(searches));
    };

    // Clean selector target
    const targetElement = document.querySelector('.search-bar');
    if (!targetElement) {
      console.warn('Algolia Custom Autocomplete: Target search form ".search-bar" not found.');
      return;
    }

    // Set class to avoid default theme styles overriding autocomplete
    targetElement.classList.add('custom-aa-initialized');
    // Ensure the search-bar is relatively positioned so our panel can anchor to it
    targetElement.style.position = 'relative';

    console.log('✅ Custom Algolia Autocomplete: Successfully bound to ".search-bar".');

    autocomplete({
      container: '.search-bar',
      placeholder: 'Cari kamera, lensa, aksesoris di DOSSINDO...',
      openOnFocus: true,
      detachedMediaQuery: 'none', // Keeps it nicely inline on mobile/desktop without breaking theme
      onSubmit({ state }) {
        if (state.query) {
          console.log(`✉️ Custom Algolia Autocomplete: Form submitted with query: "${state.query}"`);
          addRecentSearch(state.query);
          window.location.href = `/search?q=${encodeURIComponent(state.query)}`;
        }
      },
      navigator: {
        navigate({ itemUrl }) {
          console.log(`🔗 Custom Algolia Autocomplete: Navigating to URL: ${itemUrl}`);
          window.location.href = itemUrl;
        },
      },
      getSources({ query }) {
        // State A: Empty query (User clicked input but has not typed yet)
        if (!query) {
          console.log('🔍 Custom Algolia Autocomplete: Input focused but empty. Showing recent searches and popular categories.');
          const recentSearches = getRecentSearches();
          const sources = [];

          // 1. Show Recent Searches if they exist
          if (recentSearches.length > 0) {
            sources.push({
              sourceId: 'recentSearches',
              getItems() {
                return recentSearches.map((item, index) => ({
                  id: `recent-${index}`,
                  title: item,
                }));
              },
              templates: {
                header({ html }) {
                  return html`<span class="aa-SourceHeaderTitle">Pencarian Terakhir</span>`;
                },
                item({ item, html }) {
                  const searchUrl = `/search?q=${encodeURIComponent(item.title)}`;
                  return html`
                    <a href="${searchUrl}" class="aa-ItemLink">
                      <div class="aa-ItemContent" style="padding: 8px 12px; display: flex; align-items: center; gap: 10px;">
                        <div class="aa-ItemIcon" style="color: #6b7280; display: flex; align-items: center;">
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div class="aa-ItemContentTitle" style="font-size: 14px; font-weight: 500; color: #374151;">
                          ${item.title}
                        </div>
                      </div>
                    </a>
                  `;
                },
              },
            });
          }

          // 2. Show Default/Popular Collections
          sources.push({
            sourceId: 'collections',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: config.collectionsIndex || 'shopify_collections',
                    query: '',
                    params: {
                      hitsPerPage: 4,
                    },
                  },
                ],
                transformResponse({ hits, results }) {
                  console.log('📦 Custom Algolia Autocomplete: Popular Categories results:', results);
                  return hits;
                }
              });
            },
            templates: {
              header({ html, items }) {
                if (items.length === 0) return null;
                return html`<span class="aa-SourceHeaderTitle">Kategori Populer</span>`;
              },
              item({ item, html }) {
                const url = `/collections/${item.handle}`;
                return html`
                  <a href="${url}" class="aa-ItemLink">
                    <div class="aa-ItemContent">
                      <div class="aa-ItemIcon">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                      </div>
                      <div class="aa-ItemContentBody">
                        <div class="aa-ItemContentTitle">${item.title}</div>
                      </div>
                    </div>
                  </a>
                `;
              },
            },
          });

          return sources;
        }

        // State B: Active query (User typing)
        console.log(`🔍 Custom Algolia Autocomplete: Active search query: "${query}"`);
        return [
          // 1. Collections Source
          {
            sourceId: 'collections',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: config.collectionsIndex || 'shopify_collections',
                    query,
                    params: {
                      hitsPerPage: 3,
                    },
                  },
                ],
                transformResponse({ hits, results }) {
                  console.log(`📦 Custom Algolia Autocomplete: Collections results for "${query}":`, results);
                  return hits;
                }
              });
            },
            templates: {
              header({ html, items }) {
                if (items.length === 0) return null;
                return html`<span class="aa-SourceHeaderTitle">Kategori</span>`;
              },
              item({ item, components, html }) {
                const url = `/collections/${item.handle}`;
                return html`
                  <a href="${url}" class="aa-ItemLink">
                    <div class="aa-ItemContent">
                      <div class="aa-ItemIcon">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                      </div>
                      <div class="aa-ItemContentBody">
                        <div class="aa-ItemContentTitle">
                          ${components.Highlight({ hit: item, attribute: 'title' })}
                        </div>
                      </div>
                    </div>
                  </a>
                `;
              },
            },
          },
          // 2. Products Source
          {
            sourceId: 'products',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: config.productsIndex || 'shopify_products',
                    query,
                    params: {
                      hitsPerPage: config.hitsPerPage || 5,
                    },
                  },
                ],
                transformResponse({ hits, results }) {
                  console.log(`📦 Custom Algolia Autocomplete: Products results for "${query}":`, results);
                  return hits;
                }
              });
            },
            templates: {
              header({ html, items }) {
                if (items.length === 0) return null;
                return html`<span class="aa-SourceHeaderTitle">Produk</span>`;
              },
              item({ item, components, html }) {
                const url = `/products/${item.handle}`;
                
                // Track recent searches on click of a product too
                const handleClick = () => {
                  addRecentSearch(query);
                };

                const imgUrl = item.image || item.sku_image_url || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_60x60.png';
                const priceFormatted = formatPrice(item.price);
                const compareAtPriceFormatted = item.compare_at_price ? formatPrice(item.compare_at_price) : null;
                const hasDiscount = compareAtPriceFormatted && parseFloat(item.compare_at_price) > parseFloat(item.price);

                return html`
                  <a href="${url}" class="aa-ItemLink aa-ProductItem" onClick="${handleClick}">
                    <div class="aa-ProductImageContainer">
                      <img src="${imgUrl}" alt="${item.title}" onerror="this.src='https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_60x60.png'" />
                    </div>
                    <div class="aa-ProductDetails">
                      <span class="aa-ProductVendor">${item.vendor || ''}</span>
                      <div class="aa-ProductTitle">
                        ${components.Highlight({ hit: item, attribute: 'title' })}
                      </div>
                      <div class="aa-ProductPrices">
                        <span class="aa-ProductPrice">${priceFormatted}</span>
                        ${hasDiscount ? html`<span class="aa-ProductComparePrice">${compareAtPriceFormatted}</span>` : ''}
                      </div>
                    </div>
                  </a>
                `;
              },
              noResults({ html }) {
                return html`<div class="aa-NoResults">Tidak ada produk yang cocok untuk "${query}"</div>`;
              }
            },
          },
        ];
      },
    });
  };

  initAutocomplete();

  // Fix panel position for sticky header
  function fixPanelPosition() {
    var panel = document.querySelector('.aa-Panel');
    var searchBar = document.querySelector('.search-bar');
    if (!panel || !searchBar) return;
    var rect = searchBar.getBoundingClientRect();
    panel.style.position = 'fixed';
    panel.style.top = (rect.bottom + 4) + 'px';
    panel.style.left = rect.left + 'px';
    panel.style.width = rect.width + 'px';
    panel.style.maxWidth = 'none';
    panel.style.right = 'auto';
  }

  // Wrap category text in inner span + set --slide-distance for smooth translateX animation
  function setCategorySlideDistances() {
    setTimeout(function() {
      document.querySelectorAll('.aa-Panel .aa-Source[data-autocomplete-source-id="collections"] .aa-ItemContentTitle').forEach(function(el) {
        // Skip if already wrapped
        if (el.querySelector('.aa-CategoryTextInner')) return;

        var text = el.textContent.trim();
        var overflow = el.scrollWidth - el.clientWidth;

        // Wrap text in inner span so we animate the span, not the overflow:hidden container
        var span = document.createElement('span');
        span.className = 'aa-CategoryTextInner';
        span.textContent = text;
        el.textContent = '';
        el.appendChild(span);

        if (overflow > 0) {
          el.style.setProperty('--slide-distance', '-' + overflow + 'px');
        }
      });
    }, 150);
  }

  // Watch for OUR panel being added or updated (query change)
  var debounceTimer;
  var panelObserver = new MutationObserver(function() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function() {
      var panel = document.querySelector('.aa-Panel');
      if (panel) {
        fixPanelPosition();
        setCategorySlideDistances();
      }
    }, 120);
  });
  panelObserver.observe(document.body, { childList: true, subtree: true });

  // Also reposition on scroll in case panel is already open
  window.addEventListener('scroll', function() {
    if (document.querySelector('.aa-Panel')) fixPanelPosition();
  }, { passive: true });

  // Auto-collapse all filter groups on first load (checked = collapsed per CDN accordion logic)
  function collapseAllFilters() {
    var checkboxes = document.querySelectorAll('.ais-dropdown-checkbox');
    if (checkboxes.length === 0) return false;
    checkboxes.forEach(function(cb) { cb.checked = true; });
    return true;
  }

  // Wait for Algolia to render the filter sidebar, then collapse
  var filterCollapseTimer = setInterval(function() {
    if (collapseAllFilters()) clearInterval(filterCollapseTimer);
  }, 100);
  setTimeout(function() { clearInterval(filterCollapseTimer); }, 5000);
});
